/**
 * One report, from "requested" to "collected".
 *
 * ── Polling stops the moment the job does ──────────────────────────────────
 *
 * `usePolledQuery` runs while the row is `pending` or `running` and its path becomes
 * `null` on any other status, which is that hook's own "there is nothing to ask yet".
 * A row that kept polling a finished report would re-read an unchanged row for ever,
 * and spec 01 §12's criterion is that a refresh does not create excess load.
 *
 * The row shows the **polled** representation once one arrives and the `202` body
 * until then, which is what makes the transition visible rather than a blank.
 *
 * ── The download control exists only for `done` ────────────────────────────
 *
 * For the other four statuses there is **no control at all** — not a disabled one —
 * and a sentence saying why, because each "why" is different and none of them is a
 * permission problem:
 *
 *   `pending` / `running`  there is no file yet; the remedy is to wait.
 *   `failed`               there will never be a file; the remedy is a new request.
 *   `expired`              there was one and retention deleted it; nothing brings it
 *                          back.
 *
 * A greyed button would assert *"the system will not let **you** do this"* in all
 * three cases and would be wrong in all three. That is `TransitionControls`' argument
 * on `/incidents`, and Task 45's about the revoke button, applied here.
 *
 * **`status` decides, not the clock.** A screen comparing `expires_at` to
 * `Date.now()` would call a live report dead on a fast laptop clock and a deleted one
 * alive on a slow one, and would be a second, weaker copy of a judgement the server
 * already published — the discipline `sla_breached` settled on `/incidents`.
 *
 * ── Nothing here ever holds a URL ──────────────────────────────────────────
 *
 * `requestDownload` hands the signed URL to `navigate` and returns a state with no
 * `url` member. See `web/src/reports/download.ts` for why the model is shaped that
 * way rather than merely used carefully.
 */

import { useCallback, useEffect, useState, type ReactNode } from 'react';

import { useI18n } from '../../i18n';
import type { MessageKey } from '../../i18n/messages';
import { reportPath } from '../../api/paths';
import type { Report } from '../../api/types';
import { useApiQuery } from '../../api/useApiQuery';
import { usePolledQuery } from '../../api/usePolledQuery';
import { useSession } from '../../auth/SessionProvider';
import { formatInstant } from '../../i18n/datetime';
import {
  canDownload,
  requestDownload,
  type DownloadState,
  type Navigate,
} from '../../reports/download';
import { figureOf, rowCountFigure, scopeEmployeeFigure, sizeFigure } from '../../reports/figure';
import { ApiErrorNote } from '../../states/ApiErrorNote';
import { StatusNote } from '../../states/StatusNote';
import { FigureValue } from './Figure';

/** How often an unfinished report is re-read. Generation is seconds to minutes. */
export const REPORT_POLL_MS = 5000;

export interface ReportRowProps {
  readonly initial: Report;
  /** Injected so a test drives the navigation without a real address bar. */
  readonly navigate?: Navigate;
}

const IN_FLIGHT: ReadonlySet<string> = new Set(['pending', 'running']);

function defaultNavigate(url: string): void {
  window.location.assign(url);
}

export function ReportRow({ initial, navigate = defaultNavigate }: ReportRowProps): ReactNode {
  const { t, locale } = useI18n();
  const { client, user } = useSession();
  const [report, setReport] = useState<Report>(initial);
  const [download, setDownload] = useState<DownloadState>({ status: 'idle' });
  // How many refusals have told us this row's status is out of date. Both the gate
  // and the token for the one-shot re-check below, so a second stale refusal fires a
  // second read rather than being swallowed as "the value did not change".
  const [staleReads, setStaleReads] = useState(0);
  // Kept apart from `download`, because "the request was refused" and "the refusal
  // told us this row's status is out of date" are two facts and only the second
  // earns the re-read sentence. A `403` on a revoked grant is a refusal that says
  // nothing about the status, and printing "this row has just been re-read" under it
  // would be a claim the screen cannot support.
  const [staleNote, setStaleNote] = useState(false);

  // ── Two reads, because they are two different jobs ────────────────────────
  //
  // The **recurring** poll runs while the row is `pending` or `running` and stops the
  // moment it is not: `usePolledQuery`'s `null` path is its own "there is nothing to
  // ask yet", and a finished report re-read every five seconds for ever is the excess
  // load spec 01 §12 makes an acceptance criterion.
  //
  // The **one-shot** re-check fires after a refusal that proves the status on screen
  // is stale. It cannot be the same hook: a `409 NOT_READY` arrives on a row whose
  // status reads `done`, which is exactly the case where the poll's path is `null`, so
  // bumping a reload token on it would re-run nothing. That deadlock was found by the
  // test that presses download on a stale `done` row.
  const path = reportPath(initial.id);
  const polled = usePolledQuery<{ data: Report }>(
    IN_FLIGHT.has(report.status) ? path : null,
    REPORT_POLL_MS,
  );
  const recheck = useApiQuery<{ data: Report }>(staleReads > 0 ? path : null, staleReads);

  // One writer for the row, so "which of the two reads is newer" is not a question a
  // renderer has to answer. Hand-checked dependency lists — there is no `react-hooks`
  // plugin in this repository (Task 53 §11 item 4) — and each effect reads exactly the
  // one body it names.
  useEffect(() => {
    if (polled.data !== null) setReport(polled.data.data);
  }, [polled.data]);
  useEffect(() => {
    if (recheck.data !== null) setReport(recheck.data.data);
  }, [recheck.data]);

  const zone = user?.timezone ?? 'UTC';

  const collect = useCallback(() => {
    setDownload({ status: 'requesting', reportId: report.id });
    setStaleNote(false);
    void requestDownload(client, report.id, navigate).then((outcome) => {
      setDownload(outcome.state);
      // A `409 NOT_READY` or a `410 EXPIRED` means the status this control was
      // derived from is provably stale. Re-read rather than guess which of the five
      // states the row moved to — the one case where a re-read is unambiguously right.
      if (outcome.staleStatus) {
        setStaleNote(true);
        setStaleReads((value) => value + 1);
      }
    });
  }, [client, navigate, report.id]);

  const statusLabel = t(`k8.reports.status.${report.status}` as MessageKey);

  return (
    /* A row of the export queue is a ruled record, not a card. Twenty exports drawn
       as twenty identical bordered boxes are the page structure the craft floor
       refuses — and they cost the reader a border and 16px of padding per row for a
       thing that is already separated from its neighbours by being a different row. */
    <article className="wp-record" data-testid={`k8-row-${report.id}`} data-status={report.status}>
      <div className="wp-record__head">
        <h3 className="wp-record__title" tabIndex={-1} data-testid={`k8-row-heading-${report.id}`}>
          {t('k8.reports.row.heading', {
            id: report.id,
            type: t(`k8.reports.type.${report.report_type}` as MessageKey),
            format: report.format,
          })}
        </h3>
        <p className="wp-record__status" data-testid={`k8-row-status-${report.id}`}>
          {statusLabel}
        </p>
      </div>

      <div className="wp-record__body">
        <dl className="wp-keys">
          <div className="wp-keys__item">
            <dt>{t('k8.reports.row.period')}</dt>
            <dd className="wp-small" data-testid={`k8-row-period-${report.id}`}>
              {/* Two absences kept apart from a range: a row whose period the server
                did not publish says so in words. `—` between two blanks would read
                as a zero-length window somebody requested. */}
              {report.period_start === undefined || report.period_end === undefined
                ? t('k8.reports.row.periodAbsent')
                : `${report.period_start} — ${report.period_end}`}
            </dd>
          </div>
          <div className="wp-keys__item">
            <dt>{t('k8.reports.row.rows')}</dt>
            <dd>
              <FigureValue figure={rowCountFigure(report)} testId={`k8-row-rows-${report.id}`} />
            </dd>
          </div>
          <div className="wp-keys__item">
            <dt>{t('k8.reports.row.size')}</dt>
            <dd>
              <FigureValue figure={sizeFigure(report)} testId={`k8-row-size-${report.id}`} />
            </dd>
          </div>
          <div className="wp-keys__item">
            <dt>{t('k8.reports.row.scope')}</dt>
            <dd>
              <FigureValue
                figure={scopeEmployeeFigure(report)}
                testId={`k8-row-scope-${report.id}`}
              />
            </dd>
          </div>
          <div className="wp-keys__item">
            <dt>{t('k8.reports.row.requested')}</dt>
            <dd className="wp-small">{formatInstant(report.requested_at, locale, zone)}</dd>
          </div>
          <div className="wp-keys__item">
            <dt>{t('k8.reports.row.downloads')}</dt>
            <dd>
              {/* `?? 0` here would be D-22 in miniature: `download_count` is not a
                required member, and "the server did not publish how many copies
                left" must never render as "no copy has ever left". */}
              <FigureValue
                figure={figureOf(report.download_count, report.status)}
                testId={`k8-row-downloads-${report.id}`}
              />
            </dd>
          </div>
        </dl>

        {canDownload(report) ? (
          <>
            <button
              className="wp-button"
              type="button"
              data-testid={`k8-download-${report.id}`}
              onClick={collect}
            >
              {download.status === 'requesting'
                ? t('k8.reports.downloading')
                : t('k8.reports.download')}
            </button>
            <p className="wp-small wp-muted" data-testid={`k8-download-ttl-${report.id}`}>
              {t('k8.reports.download.ttlNote')}
            </p>
          </>
        ) : (
          // No control at all, and specifically not a greyed one. Three different
          // reasons, three different sentences, none of them about permission.
          <StatusNote severity="info" title={statusLabel} testId={`k8-nocontrol-${report.id}`}>
            {/* Its own test id, and the reason is a first-round mutation survivor: an
              assertion on the whole note also read the `StatusNote` title, which is
              the status label and differs per status — so "failed and expired say
              different things" passed even when their two sentences were identical.
              The neighbouring-element trap Tasks 45 and 53 each hit once. */}
            <p data-testid={`k8-nocontrol-body-${report.id}`}>
              {report.status === 'failed'
                ? t('k8.reports.noControl.failed')
                : report.status === 'expired'
                  ? t('k8.reports.noControl.expired')
                  : t('k8.reports.noControl.pending')}
            </p>
            {report.status === 'failed' && typeof report.failure_code === 'string' ? (
              <p className="wp-small">
                {t('k8.reports.row.failure')}: <code>{report.failure_code}</code>
              </p>
            ) : null}
          </StatusNote>
        )}

        {download.status === 'granted' ? (
          <p className="wp-small" role="status" data-testid={`k8-download-granted-${report.id}`}>
            {download.filename === null
              ? t('k8.reports.download.grantedNoName', {
                  expiresAt: formatInstant(download.expiresAt, locale, zone),
                })
              : t('k8.reports.download.granted', {
                  filename: download.filename,
                  expiresAt: formatInstant(download.expiresAt, locale, zone),
                })}
          </p>
        ) : null}

        {download.status === 'refused' ? (
          <>
            <ApiErrorNote
              error={download.error}
              subject={t('nav.reports')}
              testId={`k8-download-error-${report.id}`}
            />
            {staleNote ? (
              <p className="wp-small wp-muted" data-testid={`k8-download-stale-${report.id}`}>
                {t('k8.reports.download.stale')}
              </p>
            ) : null}
          </>
        ) : null}

        {polled.status === 'error' && polled.error !== null ? (
          <ApiErrorNote
            error={polled.error}
            onRetry={polled.reload}
            subject={t('nav.reports')}
            testId={`k8-row-error-${report.id}`}
          />
        ) : null}
      </div>
    </article>
  );
}
